import { Formik, Form } from "formik";
import * as Yup from "yup";
import { Button } from "@volca/ui/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@volca/ui/components/ui/dialog";
import { MoneyInput } from "@volca/ui/components/ui/money-input";
import { Stack } from "@volca/ui/components/ui/stack";
import { SwitchWithLabel } from "@volca/ui/components/ui/switch-with-label";
import { Text } from "@volca/ui/components/ui/text";
import { toast } from "@volca/ui/lib/toast";
import { parseMaskedDollarInput } from "@/core/utils/money";
import { useUpdateAppSettings } from "@/internal-transactions/query";

const schema = Yup.object({
  autoRefillActive: Yup.boolean(),
  refillThreshold: Yup.string().when("autoRefillActive", (active, s) =>
    active
      ? s
          .required("Refill threshold is required")
          .test(
            "is-valid-amount",
            "Minimum threshold is $10.00 and maximum is $10,000.00",
            (value) => {
              if (!value) return false;
              const parsed = parseMaskedDollarInput(value);
              return parsed !== null && parsed >= 10 && parsed <= 10000;
            },
          )
      : s.notRequired(),
  ),
  refillAmount: Yup.string().when("autoRefillActive", (active, s) =>
    active
      ? s
          .required("Refill amount is required")
          .test(
            "is-valid-amount",
            "Minimum refill amount is $10.00 and maximum is $10,000.00",
            (value) => {
              if (!value) return false;
              const parsed = parseMaskedDollarInput(value);
              return parsed !== null && parsed >= 10 && parsed <= 10000;
            },
          )
      : s.notRequired(),
  ),
});

interface AutoRefillBalanceModalProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  businessId?: string | null;
  currentSettings?: {
    auto_refill_active?: boolean;
    refill_amount?: number;
    refill_threshold?: number;
  };
}

const AutoRefillBalanceModal = ({
  isOpen,
  onOpenChange,
  businessId,
  currentSettings,
}: AutoRefillBalanceModalProps) => {
  const { mutate: updateAppSettings } = useUpdateAppSettings(businessId);

  const initialValues = {
    autoRefillActive: currentSettings?.auto_refill_active ?? false,
    refillThreshold: currentSettings?.refill_threshold
      ? String(currentSettings.refill_threshold / 100)
      : "",
    refillAmount: currentSettings?.refill_amount
      ? String(currentSettings.refill_amount / 100)
      : "",
  };

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogDescription className="sr-only">
          Configure auto-refill settings
        </DialogDescription>
        <Formik
          initialValues={initialValues}
          validationSchema={schema}
          enableReinitialize
          onSubmit={async (values, { setSubmitting }) => {
            // Parse values (they should already be numbers from Yup transform, but being defensive)
            const thresholdValue =
              typeof values.refillThreshold === "string"
                ? parseMaskedDollarInput(values.refillThreshold)
                : values.refillThreshold;
            const amountValue =
              typeof values.refillAmount === "string"
                ? parseMaskedDollarInput(values.refillAmount)
                : values.refillAmount;

            return updateAppSettings(
              {
                auto_refill_active: values.autoRefillActive,
                refill_threshold:
                  values.autoRefillActive && thresholdValue
                    ? Math.round(thresholdValue * 100)
                    : undefined,
                refill_amount:
                  values.autoRefillActive && amountValue
                    ? Math.round(amountValue * 100)
                    : undefined,
              },
              {
                onSuccess: () => {
                  toast.success("Auto-refill settings updated successfully");
                  setSubmitting(false);
                  onOpenChange(false);
                },
                onError: (error) => {
                  toast.error("Failed to update auto-refill settings", {
                    description:
                      error instanceof Error
                        ? error.message
                        : "Please try again",
                  });
                  setSubmitting(false);
                },
              },
            );
          }}
        >
          {({
            isSubmitting,
            isValid,
            errors,
            touched,
            values,
            setFieldValue,
            dirty,
          }) => (
            <Form className="grid gap-4">
              <DialogHeader>
                <DialogTitle>Auto-refill balance</DialogTitle>
              </DialogHeader>

              <Stack align="start" className="w-full" gap={6}>
                <Stack gap={4} direction="row" align="center" justify="between">
                  <SwitchWithLabel
                    label="Auto-refill my balance"
                    checked={values.autoRefillActive}
                    onChange={(checked) =>
                      setFieldValue("autoRefillActive", checked)
                    }
                  />
                </Stack>

                <Stack className="w-full" align="start" gap={2}>
                  <Text variant="label">Refill when balance falls below</Text>
                  <MoneyInput
                    error={
                      touched.refillThreshold
                        ? errors.refillThreshold
                        : undefined
                    }
                    name="refillThreshold"
                    value={values.refillThreshold}
                    onChange={(value) =>
                      setFieldValue("refillThreshold", value)
                    }
                    required={!!values.autoRefillActive}
                  />
                </Stack>

                <Stack className="w-full" align="start" gap={2}>
                  <Text variant="label">Refill amount</Text>
                  <MoneyInput
                    error={
                      touched.refillAmount ? errors.refillAmount : undefined
                    }
                    name="refillAmount"
                    value={values.refillAmount}
                    onChange={(value) => setFieldValue("refillAmount", value)}
                    required={!!values.autoRefillActive}
                  />
                </Stack>
              </Stack>

              <DialogFooter>
                <Button
                  variant="outline"
                  type="button"
                  onClick={() => onOpenChange(false)}
                  disabled={isSubmitting}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={!isValid || !dirty || isSubmitting}
                  loading={isSubmitting}
                  className="min-w-[108px]"
                >
                  Save
                </Button>
              </DialogFooter>
            </Form>
          )}
        </Formik>
      </DialogContent>
    </Dialog>
  );
};

export default AutoRefillBalanceModal;
